import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { redirect, type ActionFunctionArgs } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { loginFormSchema, type LoginFormValues } from "@/schemas/login";
import { useLoginMutation } from "@/hooks/useLoginMutation";
import { getUnknownErrorMessage } from "@/lib/error-message";
import { memo } from "react";

export async function loginLoader() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session) {
    return redirect("/");
  }
  return null;
}

/** Kept for bookmarked POSTs; UI submits via useLoginMutation. */
export async function loginAction({ request }: ActionFunctionArgs) {
  if (request.method !== "POST") return null;
  return redirect("/login");
}

const ErrorAlert = memo(function ErrorAlert({ error }: { error: string }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Error</AlertTitle>
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );
});

export default function Login() {
  const mutation = useLoginMutation();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (values: LoginFormValues) => {
    mutation.mutate(values);
  };

  const serverError =
    mutation.isError && mutation.error
      ? getUnknownErrorMessage(mutation.error)
      : null;

  return (
    <div
      className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-cover bg-center p-4"
      style={{
        backgroundImage: "url('/images/erp-bg.webp')",
      }}
    >
      <div className="absolute inset-0 bg-[hsl(198_24%_7%/0.88)]" aria-hidden />
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,hsl(198_28%_5%/0.55)_0%,transparent_45%,hsl(195_20%_4%/0.75)_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(hsl(0_0%_100%_/_0.12)_1px,transparent_1px),linear-gradient(90deg,hsl(0_0%_100%_/_0.12)_1px,transparent_1px)] [background-size:28px_28px]"
        aria-hidden
      />

      <div className="relative z-10 w-full max-w-sm space-y-6 rounded-lg border border-border border-t-2 border-t-[var(--chart-2)] bg-card/95 p-8 shadow-sm backdrop-blur-md motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-300">
        <div className="space-y-3 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Welcome back
          </h1>
          <div
            className="mx-auto h-1 w-9 rounded-full bg-[var(--chart-2)]/85"
            aria-hidden
          />
          <p className="text-sm text-muted-foreground">
            Enter your credentials to access the ERP
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <FormControl>
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@seo.tool"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <FormControl>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError ? <ErrorAlert error={serverError} /> : null}

            <Button
              type="submit"
              className="w-full cursor-pointer"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
